const PICK2_RULES_VERSION = "florida-pick2-v2026-07";

const PICK2_RULES = {
  straightPrize: 50,
  boxPrize: 25,
  frontBackPrize: 5,
  fireballStraightPrize: 30,
  fireballBoxPrize: 15,
  fireballFrontBackPrize: 3,
};

function toNum(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function roundMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function pickRuleValue(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return undefined;
}

function toBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    if (["false", "0", "no", "off"].includes(text)) return false;
    if (["true", "1", "yes", "on"].includes(text)) return true;
  }
  return !!value;
}

function sameMultiset(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return [...a].sort((x, y) => x - y).join("-") === [...b].sort((x, y) => x - y).join("-");
}

function uniqueFireballVariants(drawNumbers, fireballDigit) {
  const fireball = Number(fireballDigit);
  if (!Number.isInteger(fireball) || fireball < 0 || fireball > 9) return [];
  const seen = new Set();
  const variants = [];
  for (let i = 0; i < drawNumbers.length; i++) {
    const next = [...drawNumbers];
    next[i] = fireball;
    const key = next.join("-");
    if (seen.has(key)) continue;
    seen.add(key);
    variants.push(next);
  }
  return variants;
}

function normalizePick2PlayType(value) {
  const text = String(value || "").trim().toLowerCase().replace(/[_-]+/g, " ");
  if (text === "box") return "Box";
  if (text === "straight/box" || text === "straight box") return "Straight/Box";
  if (text === "front" || text === "front number") return "Front Number";
  if (text === "back" || text === "back number") return "Back Number";
  if (text === "front/back" || text === "front back") return "Front/Back";
  return "Straight";
}

function normalizePick2Rules(entry, fallbackRules = {}) {
  const nested = entry?.pickRules || entry?.rules || entry?.playRules || {};
  const source = nested && typeof nested === "object" ? nested : {};
  const fallback = fallbackRules && typeof fallbackRules === "object" ? fallbackRules : {};
  const playType = normalizePick2PlayType(pickRuleValue(
    entry?.playType,
    entry?.play_type,
    source.playType,
    source.play_type,
    fallback.playType,
    fallback.play_type,
    "Straight"
  ));
  const rawWager = Number(pickRuleValue(entry?.wager, entry?.amount, source.wager, source.amount, fallback.wager, fallback.amount, 1));
  const rawMultiple = Number(pickRuleValue(entry?.multiple, entry?.multiplier, source.multiple, source.multiplier, fallback.multiple, fallback.multiplier, 1));
  const rawCost = pickRuleValue(entry?.cost, entry?.representedCost, source.cost, source.representedCost);
  const rawTickets = Number(pickRuleValue(entry?.representedTickets, entry?.represented_plays, source.representedTickets, source.represented_plays));
  const wager = Number.isFinite(rawWager) && rawWager > 0 ? rawWager : 1;
  return {
    playType,
    wager: wager === 0.5 ? 0.5 : 1,
    multiple: Number.isFinite(rawMultiple) && rawMultiple > 0 ? rawMultiple : 1,
    fireball: toBool(pickRuleValue(entry?.fireball, entry?.fireballEnabled, source.fireball, source.fireballEnabled, fallback.fireball, fallback.fireballEnabled, false)),
    cost: rawCost === undefined ? null : toNum(rawCost, null),
    representedTickets: Number.isFinite(rawTickets) && rawTickets >= 0 ? rawTickets : null,
  };
}

function pick2LineCost(rules) {
  if (rules.cost !== null && rules.cost >= 0) return rules.cost;
  const ticketWager = rules.playType === "Straight/Box" ? 1 : rules.wager;
  return ticketWager * rules.multiple * (rules.fireball ? 2 : 1);
}

function pick2RepresentedTickets(rules) {
  if (rules.representedTickets !== null && rules.representedTickets >= 0) return rules.representedTickets;
  return rules.multiple;
}

function makePath(common, winPath, payoutBeforeMultiplier, category) {
  const before = roundMoney(payoutBeforeMultiplier);
  return {
    ...common,
    win_path: winPath,
    category,
    payout_before_multiplier: before,
    multiplier: common.multiple,
    final_payout: roundMoney(before * common.multiple),
  };
}

function scorePick2Line(entry, draw, fallbackRules = {}, index = 0) {
  const numbers = Array.isArray(entry?.numbers) ? entry.numbers.map(Number).filter(Number.isFinite) : [];
  const drawNumbers = Array.isArray(draw?.numbers) ? draw.numbers.map(Number).filter(Number.isFinite) : [];
  if (numbers.length !== 2 || drawNumbers.length !== 2) return null;
  const rawFireball = draw?.fireball_digit;
  const fireball = rawFireball === null || rawFireball === undefined || rawFireball === "" ? NaN : Number(rawFireball);
  const rules = normalizePick2Rules(entry, fallbackRules);
  const type = rules.playType;
  const validBox = numbers[0] !== numbers[1];
  const ticketWager = type === "Straight/Box" ? 1 : rules.wager;
  const splitWager = type === "Straight/Box" ? 0.5 : ticketWager;
  const exact = numbers[0] === drawNumbers[0] && numbers[1] === drawNumbers[1];
  const boxed = sameMultiset(numbers, drawNumbers);
  const variants = rules.fireball ? uniqueFireballVariants(drawNumbers, fireball) : [];
  const playKey = numbers.join("-");
  const playBoxKey = [...numbers].sort((a, b) => a - b).join("-");
  const fireballExactCount = variants.filter(variant => variant.join("-") === playKey).length;
  const fireballBoxCount = validBox
    ? variants.filter(variant => variant.join("-") !== playKey && [...variant].sort((a, b) => a - b).join("-") === playBoxKey).length
    : 0;
  const frontFireballCount = variants.filter(variant => numbers[0] === variant[0]).length;
  const backFireballCount = variants.filter(variant => numbers[1] === variant[1]).length;
  const bestMatch = numbers.reduce((sum, n, i) => sum + (n === drawNumbers[i] ? 1 : 0), 0);
  const common = {
    saved_digits: numbers,
    saved_line_index: index + 1,
    play_type: type,
    wager: rules.wager,
    multiple: rules.multiple,
    fireball_enabled: !!rules.fireball,
    official_base_digits: drawNumbers,
    official_fireball_digit: Number.isInteger(fireball) ? fireball : null,
    permutation_count: validBox ? 2 : 1,
  };
  const paths = [];

  if (type === "Straight") {
    if (exact) paths.push(makePath(common, "Base Straight", PICK2_RULES.straightPrize * ticketWager, "straight"));
    for (let i = 0; i < fireballExactCount; i++) paths.push(makePath(common, "Fireball Straight", PICK2_RULES.fireballStraightPrize * ticketWager, "fireball"));
  } else if (type === "Box") {
    if (boxed && validBox) paths.push(makePath(common, "Base Box", PICK2_RULES.boxPrize * ticketWager, "box"));
    for (let i = 0; i < fireballBoxCount; i++) paths.push(makePath(common, "Fireball Box", PICK2_RULES.fireballBoxPrize * ticketWager, "fireball"));
  } else if (type === "Straight/Box") {
    if (exact) paths.push(makePath(common, "Straight/Box Straight portion", PICK2_RULES.straightPrize * 0.75, "straight_box"));
    if (boxed && validBox) paths.push(makePath(common, "Straight/Box Box portion", PICK2_RULES.boxPrize * splitWager, "straight_box"));
    for (let i = 0; i < fireballExactCount; i++) paths.push(makePath(common, "Fireball Straight/Box Straight portion", PICK2_RULES.fireballStraightPrize * 0.75, "fireball"));
    for (let i = 0; i < fireballBoxCount; i++) paths.push(makePath(common, "Fireball Straight/Box Box portion", PICK2_RULES.fireballBoxPrize * splitWager, "fireball"));
  } else if (type === "Front Number") {
    if (numbers[0] === drawNumbers[0]) paths.push(makePath(common, "Base Front Number", PICK2_RULES.frontBackPrize * ticketWager, "front_back"));
    for (let i = 0; i < frontFireballCount; i++) paths.push(makePath(common, "Fireball Front Number", PICK2_RULES.fireballFrontBackPrize * ticketWager, "fireball"));
  } else if (type === "Back Number") {
    if (numbers[1] === drawNumbers[1]) paths.push(makePath(common, "Base Back Number", PICK2_RULES.frontBackPrize * ticketWager, "front_back"));
    for (let i = 0; i < backFireballCount; i++) paths.push(makePath(common, "Fireball Back Number", PICK2_RULES.fireballFrontBackPrize * ticketWager, "fireball"));
  } else if (type === "Front/Back") {
    if (numbers[0] === drawNumbers[0]) paths.push(makePath(common, "Base Front Number", PICK2_RULES.frontBackPrize * ticketWager, "front_back"));
    if (numbers[1] === drawNumbers[1]) paths.push(makePath(common, "Base Back Number", PICK2_RULES.frontBackPrize * ticketWager, "front_back"));
    for (let i = 0; i < frontFireballCount; i++) paths.push(makePath(common, "Fireball Front Number", PICK2_RULES.fireballFrontBackPrize * ticketWager, "fireball"));
    for (let i = 0; i < backFireballCount; i++) paths.push(makePath(common, "Fireball Back Number", PICK2_RULES.fireballFrontBackPrize * ticketWager, "fireball"));
  }

  const base = paths.filter(path => path.category !== "fireball").reduce((sum, path) => sum + path.final_payout, 0);
  const fireballAmount = paths.filter(path => path.category === "fireball").reduce((sum, path) => sum + path.final_payout, 0);
  const cash = roundMoney(base + fireballAmount);
  return {
    numbers,
    rules,
    paths: paths.filter(path => path.final_payout > 0),
    base: roundMoney(base),
    fireball: roundMoney(fireballAmount),
    cash,
    bestMatch: cash > 0 ? 2 : bestMatch,
    lineCost: roundMoney(pick2LineCost(rules)),
    representedTickets: pick2RepresentedTickets(rules),
    permutationCount: validBox ? 2 : 1,
  };
}

function emptyPick2MatchCounts() {
  return { "0": 0, "1": 0, "2": 0 };
}

function addBucket(buckets, key, details, cost, represented) {
  if (!buckets[key]) {
    buckets[key] = { label: key, saved_lines: 0, represented_tickets: 0, cost: 0, cash: 0, base_cash: 0, fireball_cash: 0, base_winning_lines: 0, fireball_winning_lines: 0 };
  }
  const bucket = buckets[key];
  bucket.saved_lines += 1;
  bucket.represented_tickets += represented;
  bucket.cost += cost;
  bucket.cash += details.cash;
  bucket.base_cash += details.base;
  bucket.fireball_cash += details.fireball;
  if (details.base > 0) bucket.base_winning_lines += 1;
  if (details.fireball > 0) bucket.fireball_winning_lines += 1;
}

function entriesRequireFireball(entries = [], fallbackRules = {}) {
  const list = Array.isArray(entries) ? entries : [];
  if (list.length) return list.some(entry => normalizePick2Rules(entry, fallbackRules).fireball);
  return normalizePick2Rules({}, fallbackRules).fireball;
}

function calculatePick2EntriesDraw(entries, draw, fallbackRules = {}) {
  const drawNumbers = Array.isArray(draw?.numbers) ? draw.numbers.map(Number).filter(Number.isFinite) : [];
  const rawFireball = draw?.fireball_digit;
  const fireball = rawFireball === null || rawFireball === undefined || rawFireball === "" ? NaN : Number(rawFireball);
  const requiresFireball = entriesRequireFireball(entries, fallbackRules);
  const hasValidFireball = Number.isInteger(fireball) && fireball >= 0 && fireball <= 9;
  if (drawNumbers.length !== 2) return { error: "Official Pick 2 draw does not have 2 winning digits" };

  const matchCounts = emptyPick2MatchCounts();
  const prizeBreakdown = {
    saved_lines_checked: 0,
    represented_tickets: 0,
    straight_cash: 0,
    box_cash: 0,
    straight_box_cash: 0,
    front_back_cash: 0,
    fireball_cash: 0,
    fireball_status: requiresFireball ? (hasValidFireball ? "scored" : "unavailable") : "not_selected",
    buckets: {},
    rule_version: PICK2_RULES_VERSION,
  };
  let savedLines = 0;
  let representedTickets = 0;
  let totalCost = 0;
  let cashWon = 0;
  let winningPlaysCount = 0;
  let bestMatch = 0;
  let largestPrize = 0;
  const winningPlayDetails = [];

  (entries || []).forEach((entry, index) => {
    const details = scorePick2Line(entry, draw, fallbackRules, index);
    if (!details) return;
    savedLines += 1;
    representedTickets += details.representedTickets;
    totalCost += details.lineCost;
    cashWon += details.cash;
    bestMatch = Math.max(bestMatch, details.bestMatch);
    largestPrize = Math.max(largestPrize, details.cash);
    matchCounts[String(Math.max(0, Math.min(2, details.bestMatch)))] += 1;
    if (details.cash > 0) {
      winningPlaysCount += 1;
      winningPlayDetails.push(...details.paths);
    }
    addBucket(prizeBreakdown.buckets, details.rules.playType, details, details.lineCost, details.representedTickets);
    if (details.rules.playType === "Straight") prizeBreakdown.straight_cash += details.base;
    else if (details.rules.playType === "Box") prizeBreakdown.box_cash += details.base;
    else if (details.rules.playType === "Straight/Box") prizeBreakdown.straight_box_cash += details.base;
    else prizeBreakdown.front_back_cash += details.base;
    prizeBreakdown.fireball_cash += details.fireball;
  });

  prizeBreakdown.saved_lines_checked = savedLines;
  prizeBreakdown.represented_tickets = representedTickets;
  for (const bucket of Object.values(prizeBreakdown.buckets)) {
    bucket.cost = roundMoney(bucket.cost);
    bucket.cash = roundMoney(bucket.cash);
    bucket.base_cash = roundMoney(bucket.base_cash);
    bucket.fireball_cash = roundMoney(bucket.fireball_cash);
  }
  ["straight_cash", "box_cash", "straight_box_cash", "front_back_cash", "fireball_cash"].forEach(key => {
    prizeBreakdown[key] = roundMoney(prizeBreakdown[key]);
  });

  return {
    play_count: savedLines,
    represented_plays: representedTickets,
    total_cost: roundMoney(totalCost),
    cash_won: roundMoney(cashWon),
    net: roundMoney(cashWon - totalCost),
    roi: totalCost > 0 ? ((cashWon - totalCost) / totalCost) * 100 : null,
    best_match: bestMatch,
    free_tickets: 0,
    largest_prize: roundMoney(largestPrize),
    winning_plays_count: winningPlaysCount,
    match_counts: matchCounts,
    prize_breakdown: prizeBreakdown,
    winning_play_details: winningPlayDetails,
    fireball_status: prizeBreakdown.fireball_status,
  };
}

module.exports = {
  PICK2_RULES,
  PICK2_RULES_VERSION,
  calculatePick2EntriesDraw,
  normalizePick2Rules,
  pick2LineCost,
  pick2RepresentedTickets,
  scorePick2Line,
  uniqueFireballVariants,
};
